import { type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Skeleton } from "~/atoms/skeleton";
import { Label } from "~/atoms/label";
import { Input } from "~/atoms/input";
import { RichTextEditor } from "~/molecules/RichTextEditor";
import { WorkflowBuilder } from "~/organisms/WorkflowBuilder";
import { type WorkflowStepInput } from "~/api/types";

interface Props {
  editedSteps: WorkflowStepInput[];
  setEditedSteps: Dispatch<SetStateAction<WorkflowStepInput[]>>;
  editedName: string;
  onNameChange: (name: string) => void;
  editedDescription: string | null;
  onDescriptionChange: (description: string | null) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  actions?: React.ReactNode;
}

export function WorkflowTemplateEditor({
  editedSteps,
  setEditedSteps,
  editedName,
  onNameChange,
  editedDescription,
  onDescriptionChange,
  setHasUnsavedChanges,
  actions,
}: Props) {
  const { t } = useTranslation();

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value);
  };

  const handleDescriptionChange = (html: string) => {
    onDescriptionChange(html || null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(($) => $.workflowTemplates.templateName)}</CardTitle>
        <Input
          value={editedName}
          onChange={handleNameChange}
          placeholder={t(($) => $.workflowTemplates.templateNamePlaceholder)}
        />
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-base font-semibold">
            {t(($) => $.commons.workflow.description)}
          </Label>
          <p className="text-muted-foreground text-sm">
            {t(($) => $.commons.workflow.descriptionDescription)}
          </p>
          <RichTextEditor
            value={editedDescription ?? ""}
            onChange={handleDescriptionChange}
            className="min-h-52"
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-base font-semibold">
            {t(($) => $.commons.workflow.steps)}
          </Label>
          <p className="text-muted-foreground text-sm">
            {t(($) => $.commons.workflow.stepsDescription)}
          </p>
          <WorkflowBuilder
            isEditMode
            hideDueDates
            steps={editedSteps}
            setSteps={setEditedSteps}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </div>
      </CardContent>
      {actions && <CardFooter>{actions}</CardFooter>}
    </Card>
  );
}

export function WorkflowTemplateEditorSkeleton() {
  return (
    <Card>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-9 w-full" />
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-52 w-full" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </CardContent>
      <CardFooter>
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-28" />
      </CardFooter>
    </Card>
  );
}
